import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CoverageMetricTableCell } from '@/features/codeCoverage/pages/CoverageMetricTableCell';
import {
  resolveCoverageMetricThresholdColor,
  type CoverageMetricThresholdKey,
} from '@/features/codeCoverage/pages/coverageMetricThresholdColors';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import {
  TableV2ActionsHeaderIcon,
  TableV2DateTimeCell,
  TableV2HeaderLabel,
  TableV2NameCell,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { buildTableV2BodyRowSurfaceSx } from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { type TableV2SkeletonColumnKind } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { formatEntityTagsLabel } from '@/features/shared/tags';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { s } from '@/theme/tokens';
import { Box, TableCell, TableRow } from '@/ui/primitives';
import { useCodeFileTableRowActions } from './useCodeFileTableRowActions';
import type { TagColor } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColumnVisibilityModel, GridSortModel } from '@/ui/primitives';
import type { CodeFileDto, MetricThreshold, QueryResult } from '@flowqa/shared';

type CoverageCodeFilesTableProps = {
  files: CodeFileDto[];
  queryKey: QueryKey;
  projectId?: string | null;
  initialSortModel?: GridSortModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  loading?: boolean;
  onColumnVisibilityModelChange?: (nextModel: GridColumnVisibilityModel) => void;
};

type CoverageCodeFileColumnKey =
  | 'file'
  | 'lineCoverage'
  | 'functionCoverage'
  | 'branchCoverage'
  | 'statementCoverage'
  | 'details'
  | 'actions';

const coverageCodeFileColumns: CoverageCodeFileColumnKey[] = [
  'file',
  'lineCoverage',
  'functionCoverage',
  'branchCoverage',
  'statementCoverage',
  'details',
  'actions',
];

const columnWidths: Record<CoverageCodeFileColumnKey, string> = {
  file: '24%',
  lineCoverage: '11%',
  functionCoverage: '11%',
  branchCoverage: '11%',
  statementCoverage: '11%',
  details: '24%',
  actions: '8%',
};

const columnLabels: Record<CoverageCodeFileColumnKey, string> = {
  file: 'File',
  lineCoverage: 'Line',
  functionCoverage: 'Function',
  branchCoverage: 'Branch',
  statementCoverage: 'Statement',
  details: getDetailLabel('details'),
  actions: getDetailLabel('actions'),
};

const skeletonColumnByKey: Record<CoverageCodeFileColumnKey, TableV2SkeletonColumnKind> = {
  file: 'name',
  lineCoverage: 'metric',
  functionCoverage: 'metric',
  branchCoverage: 'metric',
  statementCoverage: 'metric',
  details: 'name',
  actions: 'actions',
};

const formatPercent = (value: number | null | undefined): string =>
  value === null || value === undefined ? '-' : `${value.toFixed(2)}%`;

const formatCountPair = (
  covered: number | null | undefined,
  total: number | null | undefined,
): string => {
  if (covered === null || covered === undefined || total === null || total === undefined) {
    return '-';
  }
  return `${String(covered)} / ${String(total)}`;
};

const compareStrings = (
  leftValue: string | null | undefined,
  rightValue: string | null | undefined,
): number => (leftValue ?? '').localeCompare(rightValue ?? '', undefined, { sensitivity: 'base' });

const compareNumbers = (
  leftValue: number | null | undefined,
  rightValue: number | null | undefined,
): number => (leftValue ?? Number.NEGATIVE_INFINITY) - (rightValue ?? Number.NEGATIVE_INFINITY);

const compareDates = (
  leftValue: Date | string | null | undefined,
  rightValue: Date | string | null | undefined,
): number => new Date(leftValue ?? 0).getTime() - new Date(rightValue ?? 0).getTime();

const resolveCoverageCodeFileSorts = (
  initialSortModel?: GridSortModel,
): GridQueryInput['sorts'] => {
  const firstSort = initialSortModel?.[0];
  return [
    {
      field: firstSort?.field ?? 'normalizedPath',
      direction: firstSort?.sort === 'desc' ? 'desc' : 'asc',
    },
  ];
};

const sortFiles = (files: CodeFileDto[], input: GridQueryInput): CodeFileDto[] => {
  const sort = input.sorts[0] ?? { field: 'normalizedPath', direction: 'asc' as const };
  const factor = sort.direction === 'asc' ? 1 : -1;

  return [...files].sort((left, right) => {
    let result = 0;
    switch (sort.field) {
      case 'fileName':
        result = compareStrings(left.fileName, right.fileName);
        break;
      case 'normalizedPath':
        result = compareStrings(left.normalizedPath, right.normalizedPath);
        break;
      case 'linesPct':
        result = compareNumbers(left.linesPct, right.linesPct);
        break;
      case 'functionsPct':
        result = compareNumbers(left.functionsPct, right.functionsPct);
        break;
      case 'branchesPct':
        result = compareNumbers(left.branchesPct, right.branchesPct);
        break;
      case 'statementsPct':
        result = compareNumbers(left.statementsPct, right.statementsPct);
        break;
      case 'capturedAt':
      case 'latestCapturedAt':
        result = compareDates(
          left.latestCapturedAt ?? left.createdAt,
          right.latestCapturedAt ?? right.createdAt,
        );
        break;
      default:
        result = compareStrings(left.fileName, right.fileName);
        break;
    }

    return result * factor;
  });
};

const isColumnVisible = (
  columnKey: CoverageCodeFileColumnKey,
  columnVisibilityModel?: GridColumnVisibilityModel,
): boolean => columnVisibilityModel?.[columnKey] !== false;

const getVisibleColumns = (
  columnVisibilityModel?: GridColumnVisibilityModel,
): CoverageCodeFileColumnKey[] =>
  coverageCodeFileColumns.filter((columnKey) => isColumnVisible(columnKey, columnVisibilityModel));

const buildColgroup = (visibleColumns: CoverageCodeFileColumnKey[]): ReactElement => (
  <colgroup>
    {visibleColumns.map((columnKey) => (
      <col key={columnKey} width={columnWidths[columnKey]} />
    ))}
  </colgroup>
);

const buildHeader = (visibleColumns: CoverageCodeFileColumnKey[]): ReactElement => (
  <TableRow>
    {visibleColumns.map((columnKey) => (
      <TableCell key={columnKey} align={columnKey === 'actions' ? 'center' : 'left'}>
        {columnKey === 'actions' ? (
          <TableV2ActionsHeaderIcon />
        ) : (
          <TableV2HeaderLabel label={columnLabels[columnKey]} />
        )}
      </TableCell>
    ))}
  </TableRow>
);

const MetricCell = ({
  covered,
  percent,
  threshold,
  total,
}: {
  covered: number | null | undefined;
  percent: number | null | undefined;
  threshold: MetricThreshold;
  total: number | null | undefined;
}): ReactElement => (
  <CoverageMetricTableCell
    primary={formatPercent(percent)}
    secondary={formatCountPair(covered, total)}
    primaryColor={resolveCoverageMetricThresholdColor(percent, threshold)}
  />
);

const CoverageCodeFileRow = ({
  columnVisibilityModel,
  isLastVisibleRow,
  onDeleteRow,
  onEditRow,
  onUpdateRowTagsColor,
  resolveThreshold,
  row,
  updateRowTagsColorPendingId,
}: {
  columnVisibilityModel?: GridColumnVisibilityModel;
  isLastVisibleRow: boolean;
  onDeleteRow: (row: CodeFileDto) => void;
  onEditRow: (row: CodeFileDto) => void;
  onUpdateRowTagsColor: (input: { row: CodeFileDto; tags: string[]; colors: TagColor[] }) => void;
  resolveThreshold: (metricKey: CoverageMetricThresholdKey) => MetricThreshold;
  row: CodeFileDto;
  updateRowTagsColorPendingId: string | null;
}): ReactElement => {
  const visibleColumns = getVisibleColumns(columnVisibilityModel);
  const capturedAt = row.latestCapturedAt ?? row.createdAt;

  return (
    <TableRow sx={buildTableV2BodyRowSurfaceSx({ hasRenderedDivider: !isLastVisibleRow })}>
      {visibleColumns.includes('file') ? (
        <TableCell>
          <TableV2NameCell
            href={`/coverage-files/${row.id}`}
            primary={row.fileName}
            secondary={row.normalizedPath}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('lineCoverage') ? (
        <TableCell>
          <MetricCell
            covered={row.linesCovered}
            percent={row.linesPct}
            threshold={resolveThreshold('line')}
            total={row.linesTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('functionCoverage') ? (
        <TableCell>
          <MetricCell
            covered={row.functionsCovered}
            percent={row.functionsPct}
            threshold={resolveThreshold('function')}
            total={row.functionsTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('branchCoverage') ? (
        <TableCell>
          <MetricCell
            covered={row.branchesCovered}
            percent={row.branchesPct}
            threshold={resolveThreshold('branch')}
            total={row.branchesTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('statementCoverage') ? (
        <TableCell>
          <MetricCell
            covered={row.statementsCovered}
            percent={row.statementsPct}
            threshold={resolveThreshold('statement')}
            total={row.statementsTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('details') ? (
        <TableCell>
          <Box sx={{ display: 'grid', gap: s('xs'), minWidth: 0 }}>
            <TableV2NameCell
              primary={row.directoryPath ?? '-'}
              secondary={formatEntityTagsLabel(row.tags)}
            />
            <TableV2DateTimeCell value={capturedAt} />
          </Box>
        </TableCell>
      ) : null}
      {visibleColumns.includes('actions') ? (
        <TableCell align="center">
          <TableActionsCell
            rowName={row.fileName}
            verticalAlign="center"
            editActionLabel={getEntityActionLabel('edit', 'Code File')}
            editTagsEntityNoun="code file"
            rowTags={row.tags}
            onEdit={() => {
              onEditRow(row);
            }}
            onUpdateTagsColor={(input) => {
              onUpdateRowTagsColor({ row, ...input });
            }}
            updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
            deleteActionLabel={getEntityActionLabel('delete', 'Code File')}
            onDelete={() => {
              onDeleteRow(row);
            }}
          />
        </TableCell>
      ) : null}
    </TableRow>
  );
};

export const CoverageCodeFilesTable = ({
  files,
  queryKey,
  projectId = null,
  initialSortModel,
  columnVisibilityModel,
  loading = false,
}: CoverageCodeFilesTableProps): ReactElement => {
  const { thresholds } = useProjectMetricThresholds(projectId);
  const [editTarget, setEditTarget] = useState<CodeFileDto | null>(null);
  const visibleColumns = useMemo(
    () => getVisibleColumns(columnVisibilityModel),
    [columnVisibilityModel],
  );
  const sorts = useMemo(() => resolveCoverageCodeFileSorts(initialSortModel), [initialSortModel]);
  const resolveThreshold = useCallback(
    (metricKey: CoverageMetricThresholdKey) => thresholds.files[metricKey],
    [thresholds.files],
  );
  const {
    deleteLoading,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useCodeFileTableRowActions({ selectedProjectId: projectId });

  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<CodeFileDto>> => {
      const sortedFiles = sortFiles(files, input);
      const page = Math.max(input.page, 1);
      const pageSize = input.pageSize;
      const startIndex = (page - 1) * pageSize;
      return await Promise.resolve({
        items: sortedFiles.slice(startIndex, startIndex + pageSize),
        total: sortedFiles.length,
        page,
        pageSize,
      });
    },
    [files],
  );

  return (
    <>
      <TableV2QueryShell
        active
        ariaLabel="Coverage Code Files"
        colgroup={buildColgroup(visibleColumns)}
        emptyText={getEmptyStateMessage('entity.noneFound', { entity: 'file' })}
        entityLabel="files"
        forceLoading={loading}
        header={buildHeader(visibleColumns)}
        pageSizeOptions={[10, 25, 50]}
        queryFn={queryFn}
        queryKey={queryKey}
        skeletonColumns={visibleColumns.map((columnKey) => skeletonColumnByKey[columnKey])}
        sorts={sorts}
        renderRow={(row, context) => (
          <CoverageCodeFileRow
            key={row.id}
            columnVisibilityModel={columnVisibilityModel}
            isLastVisibleRow={context.isLastVisibleRow}
            resolveThreshold={resolveThreshold}
            row={row}
            updateRowTagsColorPendingId={updateRowTagsColorPendingId}
            onDeleteRow={onDeleteRow}
            onEditRow={setEditTarget}
            onUpdateRowTagsColor={onUpdateRowTagsColor}
          />
        )}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'codeFile' })}
        description="This code file entry will be removed from the coverage file list."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'codeFile' })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Code File"
        entityName={editTarget?.fileName ?? ''}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        loading={updateRowTagsColorPendingId === (editTarget?.id ?? null)}
        onClose={() => {
          if (updateRowTagsColorPendingId === (editTarget?.id ?? null)) return;
          setEditTarget(null);
        }}
        onSave={({ tags, colors }) => {
          if (!editTarget) return;
          onUpdateRowTagsColor({ row: editTarget, tags, colors });
          setEditTarget(null);
        }}
      />
    </>
  );
};
